<script setup lang="ts">
const props = defineProps<{
  media: HTMLMediaElement | null
}>()

const media = toRef(props, 'media')

const controls = useMediaControls(media)

const activeMedia = useActiveMedia()

function getCurrentDuration() {
  return media.value?.duration ?? 0
}

const duration = ref(getCurrentDuration())

useEventListener(media, 'durationchange', () => {
  duration.value = getCurrentDuration()
})

useEventListener(media, 'play', () => {
  activeMedia.value = media.value
})

watch(activeMedia, (value) => {
  if (value !== media.value && !media.value?.paused) {
    media.value?.pause()
  }
})

const isPlaying = useDebounce(
  computed(() => controls.playing.value || controls.seeking.value),
  100,
)

async function togglePlay() {
  if (media.value?.paused) {
    await media.value?.play()
  }
  else if (media.value) {
    media.value.pause()
  }
}

function getCurrentTime() {
  return media.value?.currentTime ?? 0
}

const visualTime = ref(getCurrentTime())

function updateVisualTime(value?: number) {
  visualTime.value = value ?? getCurrentTime()
}

const { ready: syncEnabled, start: disableSyncForASecond } = useTimeout(1000, { controls: true, immediate: false })

// We want to keep the visual time in sync with the media time, but we want to block that sync for a while after scrobbling, since the updates may come in the "wrong" order, which makes the experience janky.
useEventListener(media, 'timeupdate', () => {
  if (syncEnabled.value) {
    updateVisualTime()
  }
})

function updateMediaTime(time: number) {
  if (media.value) {
    media.value.currentTime = Math.min(
      Math.max(0, time),
      media.value.duration,
    )
  }
}

const updateMediaTimeDebounced = useDebounceFn(updateMediaTime, 300)

const currentTime = computed({
  get: () => visualTime.value,
  set: (time: number) => {
    // 1. Disable time syncing for a bit so that visual time takes precedence
    disableSyncForASecond()
    // 2. Update visual time immediately
    updateVisualTime(time)
    // 3. Debounce updating the media time so that the media doesn't hop and chop when the user e.g. scrobbles the timeline.
    updateMediaTimeDebounced(time)
  },
})

function seek(deltaSeconds: number) {
  // When seeking directly, e.g. when clicking seek buttons, we _do_ want to update the media time immediately, so the user doesn't have to wait for the change.
  updateMediaTime(currentTime.value + deltaSeconds)
}

provide(diKeys.mediaControls, {
  togglePlay,
  seek,
  duration: readonly(duration),
  currentTime,
})
</script>

<template>
  <slot :is-playing="isPlaying" />
</template>
